import {Injectable} from '@angular/core';
import {JwtHelper} from './jwt-helper';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {CookieService} from 'app/authentication/access-token/cookie.service';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/timer';
import {filter, map, switchMap, mapTo} from 'rxjs/operators';

const TOKEN_KEY = 'token';

export interface Token {
  exp: number;
}


@Injectable()
export class AccessTokenService<T extends Token> {
  decodedToken: Observable<T>;
  expired: Observable<string>;

  private token = new BehaviorSubject<string | undefined>(undefined);
  private jwtHelper = new JwtHelper();

  constructor(private cookieService: CookieService) {
    const tokenFromCookie = cookieService.getCookie(TOKEN_KEY);

    if (tokenFromCookie && !this.jwtHelper.isTokenExpired(tokenFromCookie)) {
      this.token.next(tokenFromCookie);
    }

    // Create an observable of the decoded token
    this.decodedToken = this.token.pipe(
      map(token => token ? <T>this.jwtHelper.decodeToken(token) : undefined)
    );

    // Wait for the token to expire
    this.expired = this.token.pipe(
      switchMap(token => this.notifyBeforeExpire(token, 0))
    );

    // Persist token in a cookie
    this.token.pipe(
      filter(token => !token)
    ).subscribe(() => cookieService.deleteCookie(TOKEN_KEY));
    this.token.pipe(
      filter(token => !!token)
    ).subscribe(token => cookieService.saveCookie(TOKEN_KEY, token, 1));
  }

  setToken(token: string): void {
    this.token.next(token);
  }

  removeToken(): void {
    this.token.next(undefined);
  }

  /**
   * Remove expired tokens
   */
  setupRemoveExpiredToken(): void {
    this.expired.subscribe(() => this.removeToken());
  }

  /**
   * Will keep fetching a refreshed access token shortly before the current access token expires.
   */
  setupTokenRefresh(getToken: Observable<string>): void {
    this.token.pipe(
      // Wait for the token to almost expire
      switchMap(token => this.notifyBeforeExpire(token)),
      // Get a new token
      switchMap(() => getToken)
    ).subscribe(this.token); // Set the new token
  }

  /**
   * Returns an observable which emits an event when the token is about to expire.
   * @param token
   * @param secondsOfNotice
   * @returns {Observable<R>}
   */
  private notifyBeforeExpire(token?: string, secondsOfNotice: number = 60): Observable<string> {
    if (!token || this.jwtHelper.isTokenExpired(token)) {
      return Observable.empty();
    }

    const expirationDate = this.jwtHelper.getTokenExpirationDate(token);
    const delay = expirationDate.getTime() - Date.now() - secondsOfNotice * 1000;

    return Observable.timer(delay).pipe(
      mapTo(token)
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/authentication/access-token/access-token.service.ts